import React from 'react';
import { ArrowsClockwise, CheckCircle, Compass, Lightning } from 'phosphor-react';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';

const FLOW_STEPS = ['MONITOR', 'DETECT', 'ADJUST', 'PROTECT'];

const FUTURE_CAPABILITIES = [
  {
    icon: 'Δ',
    title: 'DYNAMIC DELTA HEDGING',
    status: 'AUTO',
    description: 'Automatically manage directional exposure as portfolio Delta changes.',
  },
  {
    icon: <ArrowsClockwise size={20} weight="bold" />,
    title: 'INTELLIGENT ROLLING',
    status: 'AUTO',
    description: 'Manage roll rules using conditions such as Delta, P&L, DTE and position state.',
  },
  {
    icon: '◈',
    title: 'MARGIN & RISK MANAGEMENT',
    status: 'AUTO',
    description: 'Monitor margin, exposure and portfolio risk against predefined rules.',
  },
  {
    icon: <Lightning size={20} weight="fill" />,
    title: 'STRATEGY AUTOMATION',
    status: '24/7',
    description: 'Define the rules. GENI monitors the portfolio and reacts when conditions are met.',
  },
];

export default function OptionsAutopilot() {
  return (
    <OptionsPageShell hideHeader>
      <main className="space-y-10" data-testid="options-autopilot-page">
        <header className="relative overflow-hidden border-b border-white/10 pb-9 pt-2" data-testid="options-autopilot-hero">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_15%,rgba(240,185,11,0.10),transparent_28%),radial-gradient(circle_at_82%_40%,rgba(168,85,247,0.08),transparent_30%)]" />
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr),280px] lg:items-end">
            <div className="max-w-4xl space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#F0B90B]/30 bg-[#F0B90B]/10 px-3 py-1.5 text-[10px] font-bold uppercase text-[#F7D47A]" data-testid="options-autopilot-coming-soon-badge">
                <Compass size={13} weight="fill" /> COMING SOON · PRO
              </div>
              <div className="space-y-2">
                <h1 className="text-4xl font-black text-white sm:text-5xl lg:text-6xl" data-testid="options-autopilot-title">OPTIONS AUTOPILOT</h1>
                <p className="text-sm font-semibold uppercase text-white/45 sm:text-base" data-testid="options-autopilot-subtitle">Automated Options Management</p>
              </div>
              <p className="max-w-3xl text-2xl font-semibold leading-tight text-white sm:text-3xl" data-testid="options-autopilot-primary-message">Your portfolio. Managed by rules, not emotions.</p>
              <p className="text-base text-white/58" data-testid="options-autopilot-complement">The next evolution of GENI Options.</p>
            </div>

            <div className="border-l border-white/10 pl-5" data-testid="options-autopilot-availability">
              <p className="text-[10px] font-bold uppercase text-white/35">Roadmap preview</p>
              <p className="mt-2 text-sm leading-6 text-white/62">Future capabilities only. Not currently available.</p>
            </div>
          </div>
        </header>

        <section className="space-y-4" data-testid="options-autopilot-flow-section">
          <p className="text-[10px] font-semibold uppercase text-white/38">Management loop</p>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="options-autopilot-flow">
            {FLOW_STEPS.map((step, index) => (
              <div key={step} className="relative flex min-h-16 items-center gap-3 border-y border-white/8 bg-white/[0.02] px-4 py-3" data-testid={`options-autopilot-flow-${step.toLowerCase()}`}>
                <CheckCircle size={18} weight="duotone" className="shrink-0 text-[#F0B90B]" />
                <span className="text-xs font-bold text-white/75">{step}</span>
                {index < FLOW_STEPS.length - 1 ? <span className="absolute -right-2.5 top-1/2 z-10 hidden -translate-y-1/2 text-white/25 lg:block">→</span> : null}
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4" data-testid="options-autopilot-capabilities-section">
          <div>
            <p className="text-[10px] font-semibold uppercase text-white/38">Future capabilities</p>
            <h2 className="mt-2 text-2xl font-bold text-white" data-testid="options-autopilot-capabilities-title">Rule-based portfolio management</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2" data-testid="options-autopilot-capabilities">
            {FUTURE_CAPABILITIES.map((capability, index) => (
              <article key={capability.title} className="min-h-40 rounded-lg border border-white/9 bg-white/[0.025] p-5" data-testid={`options-autopilot-capability-${index + 1}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3 text-white/90">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/10 bg-black/20 text-lg font-black text-[#F7D47A]">{capability.icon}</span>
                    <h3 className="text-sm font-bold text-white">{capability.title}</h3>
                  </div>
                  <span className="shrink-0 rounded border border-[#A855F7]/25 bg-[#A855F7]/10 px-2 py-1 text-[9px] font-bold text-[#D8B4FE]">{capability.status}</span>
                </div>
                <p className="mt-5 max-w-xl text-sm leading-6 text-white/58">{capability.description}</p>
              </article>
            ))}
          </div>
        </section>

        <footer className="flex flex-col gap-4 border-t border-white/10 py-7 sm:flex-row sm:items-end sm:justify-between" data-testid="options-autopilot-footer">
          <p className="text-lg font-semibold leading-7 text-white" data-testid="options-autopilot-closing-message">Build your strategy.<br />Define the rules.<br />Let GENI manage the execution.</p>
          <p className="text-sm font-semibold text-[#F7D47A]" data-testid="options-autopilot-footer-coming-soon">Coming Soon for GENI Options PRO</p>
        </footer>
      </main>
    </OptionsPageShell>
  );
}